import { computed, h, markRaw, ref } from "vue";
import { Gavel } from "lucide-vue-next";
import { allowWMaticMinAmount } from "~/application/config";
import { useStoreUserV2 } from "@/stores/storeUserV2";
import { defineStore } from "pinia";
import mitt from "mitt";
import { IAuctionItem } from "~/common/interfaces/IMarketplace";
import { AuctionWonEventPayload, MercureEventType, NftEquipment, NftSkill } from "fungi-types";
import { useI18n } from "vue-i18n";
import { showNotification, ToastType } from "~/shadcn/components/ui/sonner/custom-toast";
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import useConvert from "~/composables/useConvert";
export type StoreAppEvent = {
  notif: { type: NotifType; auctionItem: IAuctionItem; text?: string };
  tick: number;
};

const { weiToMatic } = useConvert;

export enum NotifType {
  TOPBID = "topbid",
  OUTBID = "outbid",
  OVERBID = "overbid",
  WIN = "win",
  BID_FAILED = "bid-failed",
  BUY_FAILED = "buy-failed",
  BUY_SUCCESS = "buy-success",
  LIST_SUCCESS = "list-success",
  LIST_FAILED = "list-failed",
  UNLIST_SUCCESS = "unlist-success",
  UNLIST_FAILED = "unlist-failed",
  FUNDS_DEPOSIT_SUCCESS = "funds-deposit-success",
  FUNDS_DEPOSIT_FAILED = "funds-deposit-failed",
}

export const useStoreApp = defineStore("app-store", () => {
  // Interval
  const debug = ref(false);
  const initialized = ref(false);

  // Status
  const actionInProgress = ref(false);

  // Modals and display status controllers
  const showNeedMatic = ref(false);
  const showSwapModal = ref(false);
  const showBidModal = ref(false);
  const showListModal = ref(false);
  const showBuyModal = ref(false);
  const showDepositModal = ref(false);
  const showTransferModal = ref(false);
  const showMigrateUsdcToSmartWalletModal = ref(false);
  const showWithdrawModal = ref(false);
  const showOnboardingModal = ref(false);
  const showRG2025Modal = ref(false);
  const showEquipmentsUpgradeModal = ref(false);
  const showUnlockEquipmentModal = ref(false);
  const showPackModal = ref(false);
  const showAuthModal = ref(false);
  const showRedirectToOldAuth = ref(false);
  const showRegistrationModal = ref(false);
  const showMigrateToUsdcModal = ref(false);
  const showMigrateToSmartWalletModal = ref(false);

  // Intervals
  const secondsTickerInterval = ref(0);
  const refreshInterval = ref({});
  const emitter = mitt<StoreAppEvent>();

  const pauseNotificationTimers = ref(false);
  function pauseAllNotificationTimers() {
    pauseNotificationTimers.value = true;
  }
  function resumeAllNotificationTimers() {
    pauseNotificationTimers.value = false;
  }

  const showSwapButton = computed(() => {
    const storeUser = useStoreUserV2();

    return (
      storeUser.isConnected &&
      (storeUser.currentMaticBalance > 0.01 || storeUser.currentWMaticBalance > 0.01)
    );
  });

  const showAuthorizeWMaticButton = computed(() => {
    const storeUser = useStoreUserV2();

    return (
      storeUser.isConnected &&
      storeUser.currentWMaticBalance > 0.01 &&
      storeUser.allowedWMaticAmount < allowWMaticMinAmount
    );
  });

  async function init() {
    if (initialized.value) return;

    window.clearInterval(secondsTickerInterval.value);

    secondsTickerInterval.value = window.setInterval(() => {
      emitter.emit("tick", Date.now());
    }, 1_000);

    initialized.value = true;
  }

  function setActionInProgress(value: boolean) {
    actionInProgress.value = value;
  }

  function setBidModalState(value: boolean) {
    showBidModal.value = value;
  }

  function setPackModalState(value: boolean) {
    showPackModal.value = value;
  }

  const { t } = useI18n();

  function displayNotification(
    type: NotifType,
    nft: NftSkill | AuctionWonEventPayload | NftEquipment,
    additionalContext?: string
  ) {
    const card = nft as NftSkill | NftEquipment;

    const cardName = (card as NftSkill).firstName
      ? `${(card as NftSkill).firstName} ${(card as NftSkill).lastName}`
      : (card as NftEquipment).title;

    const cardSkill = (card as NftSkill).skill ? t(`skills.${(card as NftSkill).skill}`) : "";
    const cardScarcity = (card as NftSkill).scarcity
      ? t(`scarcities.${(card as NftSkill).scarcity}`)
      : "";

    switch (type) {
      case NotifType.OUTBID: {
        const storeMkp = useStoreMarketplace();

        showNotification({
          type: ToastType.WARNING,
          message: t(
            "messages.outbid",
            {
              cardName: `${cardName}`,
              skill: cardSkill,
              scarcity: cardScarcity,
            },
            "You've been outbid"
          ),
          duration: 10_000,
          image: card?.image || undefined,
          icon: markRaw(h(Gavel)),
          callback: () => {
            storeMkp.selectedItem = nft;
            setBidModalState(true);
          },
        });
        break;
      }

      case NotifType.WIN: {
        const data = nft as AuctionWonEventPayload;
        showNotification({
          type: ToastType.SUCCESS,
          message: t("messages.bid-win", {
            cardName: data.card.title,
            skill: data.card.skill || "",
            scarcity: data.card.scarcity || "",
          }).replace(/ {2,}/g, " "),
          duration: 5_000,
          image: data.card.image,
        });
        break;
      }

      case NotifType.TOPBID: {
        showNotification({
          type: ToastType.INFO,
          message: t(
            "messages.bid-success",
            {
              cardName: `${cardName}`,
              skill: cardSkill,
              scarcity: cardScarcity,
            },
            "You are the highest bidder on this card!"
          ),
          duration: 5_000,
          image: nft.image,
        });
        break;
      }

      case NotifType.OVERBID: {
        showNotification({
          type: ToastType.INFO,
          message: t(
            "messages.bid-over",
            {
              cardName: `${cardName}`,
              skill: cardSkill,
              scarcity: cardScarcity,
            },
            "You have raised your bid!"
          ),
          duration: 5_000,
          image: nft.image,
        });
        break;
      }

      case NotifType.BID_FAILED: {
        showNotification({
          title: additionalContext ? t("messages.bid-failed") : undefined,
          message: additionalContext ?? t("messages.bid-failed"),
          duration: 10_000,
          type: ToastType.ERROR,
        });
        break;
      }

      case NotifType.BUY_SUCCESS: {
        showNotification({
          title: t("messages.buy-success"),
          message: t("messages.buy-success-details", {
            cardName: `${cardName}`,
            skill: cardSkill,
            scarcity: cardScarcity,
          }),
          type: ToastType.SUCCESS,
          duration: 3_000,
        });
        break;
      }

      case NotifType.BUY_FAILED: {
        showNotification({
          title: additionalContext ? t("messages.bid-failed") : undefined,
          message: additionalContext ?? t("messages.buy-failed"),
          type: ToastType.ERROR,
          duration: 5_000,
        });
        break;
      }

      case NotifType.LIST_SUCCESS: {
        showNotification({
          title: t("messages.list-success"),
          message: t("messages.list-success-details", {
            cardName: `${cardName}`,
            skill: cardSkill,
            scarcity: cardScarcity,
            price: weiToMatic(nft?.auction?.startedAmount || 0),
          }),
          image: card.image,
          type: ToastType.INFO,
          duration: 4_000,
        });
        break;
      }

      case NotifType.LIST_FAILED: {
        showNotification({
          title: t("messages.list-failed"),
          message: t("messages.list-failed-details", {
            cardName: `${cardName}`,
            skill: cardSkill,
            scarcity: cardScarcity,
          }).replace(/ {2}/g, " "),
          type: ToastType.ERROR,
          duration: 5_000,
        });
        break;
      }

      case NotifType.UNLIST_SUCCESS: {
        showNotification({
          title: t("messages.unlist-success"),
          message: t("messages.unlist-success-details", {
            cardName: `${cardName}`,
            skill: cardSkill,
            scarcity: cardScarcity,
          }),
          image: card.image,
          type: ToastType.INFO,
          duration: 3_000,
        });
        break;
      }

      case NotifType.UNLIST_FAILED: {
        showNotification({
          title: additionalContext ? t("messages.unlist-failed") : undefined,
          message: t("messages.unlist-failed-details", {
            cardName: `${cardName}`,
            skill: cardSkill,
            scarcity: cardScarcity,
          }).replace(/ {2}/g, " "),
          type: ToastType.ERROR,
          duration: 5_000,
        });
        break;
      }

      case NotifType.FUNDS_DEPOSIT_SUCCESS: {
        showNotification({
          title: t("messages.funds-deposit-success"),
          message: t("messages.funds-deposit-success-details"),
          type: ToastType.SUCCESS,
          duration: 5_000,
        });
        break;
      }

      case NotifType.FUNDS_DEPOSIT_FAILED: {
        showNotification({
          title: t("messages.funds-deposit-failed"),
          message: t("messages.funds-deposit-failed-details"),
          type: ToastType.ERROR,
          duration: 5_000,
        });
        break;
      }

      default: {
        console.warn(`Received unknown notification type: ${type}`);
      }
    }
  }

  return {
    debug,
    actionInProgress,
    showNeedMatic,
    showSwapModal,
    showBidModal,
    showListModal,
    showBuyModal,
    showTransferModal,
    showMigrateUsdcToSmartWalletModal,
    showDepositModal,
    showWithdrawModal,
    showOnboardingModal,
    showRG2025Modal,
    showEquipmentsUpgradeModal,
    showUnlockEquipmentModal,
    showPackModal,
    showAuthModal,
    showRedirectToOldAuth,
    showRegistrationModal,
    showMigrateToUsdcModal,
    showMigrateToSmartWalletModal,
    // mercureService,
    refreshInterval,
    emitter,
    showSwapButton,
    showAuthorizeWMaticButton,
    pauseNotificationTimers,
    pauseAllNotificationTimers,
    resumeAllNotificationTimers,
    init,
    initialized,
    setActionInProgress,
    setBidModalState,
    setPackModalState,
    displayNotification,
  };
});
